import React, { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Building2, TrendingUp, Users, Award, Package, Headphones, CheckCircle } from "lucide-react";

const BecomeDistributor = () => {
  const [formData, setFormData] = useState({
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    location: "",
    businessType: "",
    experience: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Form submission logic will be added later
    alert("Thank you for your interest! We'll contact you within 2 business days.");
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: "Competitive Wholesale Pricing",
      description: "Access exclusive distributor pricing with attractive margins on our complete product portfolio."
    },
    {
      icon: Package,
      title: "Extensive Product Range",
      description: "Offer your customers premium flooring solutions from leading manufacturers with warranty support."
    },
    {
      icon: Users,
      title: "Dedicated Support Team",
      description: "Receive personalized assistance from our experienced sales and technical support specialists."
    },
    {
      icon: Award,
      title: "Marketing Resources",
      description: "Benefit from professional marketing materials, product samples, and digital assets to grow your business."
    },
    {
      icon: Building2,
      title: "Territory Protection",
      description: "Enjoy protected territory rights to build your customer base across British Columbia."
    },
    {
      icon: Headphones,
      title: "Training & Education",
      description: "Access comprehensive product training, installation guidance, and industry best practices."
    }
  ];

  const requirements = [
    "Established flooring retail or contracting business in Canada",
    "Physical showroom or retail location preferred",
    "Proven track record in the flooring industry",
    "Commitment to stocking and displaying our products",
    "Valid business license and GST/HST registration",
    "Ability to meet minimum order requirements"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-luxury-navy via-luxury-charcoal to-luxury-navy py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(212, 175, 55, 0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-24 h-1 bg-luxury-gold mx-auto mb-8"></div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 luxury-heading">
              Become a <span className="text-luxury-gold">Distributor</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Partner with Timberlake Flooring and bring premium flooring solutions to your customers across Canada. 
              Join our growing network of authorized distributors.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-luxury-navy luxury-heading mb-4">
              Partnership Benefits
            </h2>
            <div className="w-24 h-1 bg-luxury-gold mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-sm border-l-4 border-luxury-gold shadow-lg hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="w-16 h-16 rounded-full bg-luxury-gold/10 flex items-center justify-center mb-6 group-hover:bg-luxury-gold/20 transition-colors">
                    <IconComponent className="w-8 h-8 text-luxury-gold" />
                  </div>
                  <h3 className="text-xl font-bold text-luxury-navy mb-3 luxury-heading">
                    {benefit.title}
                  </h3>
                  <p className="text-luxury-slate leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-luxury-navy luxury-heading mb-4">
              Distributor Requirements
            </h2>
            <div className="w-24 h-1 bg-luxury-gold mx-auto mb-6"></div>
            <p className="text-lg text-luxury-slate">
              We're looking for established businesses committed to excellence in the flooring industry.
            </p>
          </div>

          <div className="bg-gradient-to-br from-luxury-gold/10 to-luxury-gold/5 border-l-4 border-luxury-gold p-8 rounded-sm">
            <ul className="space-y-4">
              {requirements.map((requirement, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-luxury-gold mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-luxury-slate text-lg">{requirement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-luxury-navy luxury-heading mb-4">
              Apply Today
            </h2>
            <div className="w-24 h-1 bg-luxury-gold mx-auto mb-6"></div>
            <p className="text-lg text-luxury-slate">
              Fill out the form below and our partnership team will contact you within 2 business days.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-sm shadow-2xl border-t-4 border-luxury-gold">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  name="businessName"
                  required
                  value={formData.businessName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="Your Company Name"
                />
              </div>

              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  required
                  value={formData.contactName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="Your Name"
                />
              </div>

              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="(604) 123-4567"
                />
              </div>

              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Business Location *
                </label>
                <input
                  type="text"
                  name="location"
                  required
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="City, Province"
                />
              </div>

              <div>
                <label className="block text-luxury-navy font-semibold mb-2">
                  Business Type *
                </label>
                <select
                  name="businessType"
                  required
                  value={formData.businessType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                >
                  <option value="">Select Type</option>
                  <option value="retailer">Flooring Retailer</option>
                  <option value="contractor">Flooring Contractor</option>
                  <option value="builder">Home Builder/Developer</option>
                  <option value="designer">Interior Designer</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-luxury-navy font-semibold mb-2">
                  Years of Experience *
                </label>
                <select
                  name="experience"
                  required
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                >
                  <option value="">Select Experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-luxury-navy font-semibold mb-2">
                  Tell Us About Your Business
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-luxury-gold transition-colors"
                  placeholder="Tell us about your business, showroom, customer base, and why you'd like to partner with Timberlake Flooring..."
                ></textarea>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                className="w-full bg-luxury-gold text-luxury-navy px-10 py-4 rounded-sm font-bold text-lg hover:bg-luxury-gold-dark transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1"
              >
                Submit Application
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-6 text-center">
              By submitting this form, you agree to be contacted by Timberlake Flooring regarding distributor opportunities.
            </p>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-luxury-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4 luxury-heading">
            Questions About Our Distributor Program?
          </h3>
          <p className="text-gray-300 mb-8 text-lg">
            Our partnership team is here to help. Contact us directly for more information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+16046163986"
              className="inline-flex items-center justify-center bg-luxury-gold text-luxury-navy px-8 py-3 rounded-sm font-semibold hover:bg-luxury-gold-dark transition-all duration-300"
            >
              Call: 604-616-3986
            </a>
            <a
              href="mailto:timberlakebuildingcenter@gmail.com"
              className="inline-flex items-center justify-center bg-white text-luxury-navy px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeDistributor;
